import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Space, Tooltip } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { dateToString, getFullName } from '@/utils';
import { useGetChildSearchDownloadMutation } from '@/services/child-api';
import { selectorPermissions } from '@/stores/auth';
import { DownloadSvg, PlusSvg, UsersSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';

const Wrapper = styled.div`
  padding: 0 24px 24px 24px;
  background-color: var(--colorBgContainer);
  border-radius: var(--borderRadius);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-block: 16px;
  padding-right: 8px;

  .title {
    font-size: 16px;
    font-weight: 500;
    margin-right: auto;
  }
`;

const columns = [
  {
    title: 'ID',
    dataIndex: 'childCode',
    render: (value, record) => <Link to={`/child/${record.childId}`}>{value}</Link>,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    render: (value) => getFullName(value),
  },
  {
    title: 'County',
    dataIndex: 'county',
    render: (value, record) => (
      <>
        <div>{value.name}</div>
        <i>{record.region.name}</i>
      </>
    ),
  },
  {
    title: 'DOB',
    dataIndex: 'birthInfo',
    render: (value) => {
      //const d = dayjs(value.date);
      return (
        <>
          <div>{dateToString(value.date)}</div>({value.age} years)
        </>
      );
    },
  },
  {
    title: 'Adoption Liaison',
    dataIndex: 'adoptionLiaison',
    render: (value) => (
      <>
        <div>{getFullName(value.name)}</div>
        <a href={`mailto:${value.emailAddress}`}>{value.emailAddress}</a>
      </>
    ),
  },
  {
    title: 'Caseworker',
    dataIndex: 'caseworker',
    render: (value) => (
      <>
        <div>{getFullName(value?.name)}</div>
        <a href={`mailto:${value?.emailAddress}`}>{value?.emailAddress}</a>
      </>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (value, record) => (
      <>
        <div>{value.name}</div>
        {!!record.remainingTasks?.length && (
          <>
            <i>Remaining Tasks</i>
            <ul style={{ margin: 0 }}>
              {record.remainingTasks.map((task) => (
                <li key={task.name}>{task.name}</li>
              ))}
            </ul>
          </>
        )}
      </>
    ),
  },
];

const SearchResults = ({ searchId, data, loading }) => {
  const permissions = useSelector(selectorPermissions);
  const [download, { isLoading: downloading }] = useGetChildSearchDownloadMutation();

  const handleDownload = async () => {
    try {
      const res = await download({ searchId }).unwrap();

      const downloadLink = document.createElement('a');
      downloadLink.href = `data:${res.contentType};base64,${res.fileBase64}`;
      downloadLink.download = res.fileName;
      downloadLink.click();
    } catch (err) {}
  };

  return (
    <Wrapper id="search-results">
      <Toolbar>
        <div className="title">Search Results</div>
        {permissions.writeChild && (
          <Button type="primary" icon={<PlusSvg />} to="/child/new">
            Add
          </Button>
        )}
        <Space>
          {permissions.writeChild && (
            <Tooltip title="Sibling Group">
              <Button type="link" icon={<UsersSvg fontSize="18" />} to="/sibling-group/new" />
            </Tooltip>
          )}
          <Tooltip title="Download Results">
            <Button
              type="link"
              icon={<DownloadSvg fontSize="18" />}
              disabled={!data?.count}
              loading={downloading}
              onClick={handleDownload}
            />
          </Tooltip>
        </Space>
      </Toolbar>

      <Table columns={columns} dataSource={data?.data} rowKey="childId" loading={loading} pageInfo={data} />
    </Wrapper>
  );
};

export default memo(SearchResults);
